import {
  BadgeCheck,
  ChevronDown,
  ChevronUp,
  Clock,
  Eye,
  FileSpreadsheet,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShoppingCart,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import AnularVentaPosModal from '../../components/pos/AnularVentaPosModal'
import DetalleVentaPosModal from '../../components/pos/DetalleVentaPosModal'
import {
  CampoFiltro,
  ChipFiltro,
  estiloInputBusqueda,
  estiloInputFiltro,
} from '../../components/pos/FiltrosPosComunes'
import ModalMensaje from '../../components/ui/ModalMensaje'
import Paginacion from '../../components/ui/Paginacion'
import { useAuth } from '../../context/AuthContext'
import { useEmpresa } from '../../context/EmpresaContext'
import { usePaginacion } from '../../hooks/usePaginacion'
import { useModalMensaje } from '../../hooks/useModalMensaje'
import { supabase } from '../../lib/supabase'
import { obtenerEstadoPago } from '../../utils/gastos'
import { normalizarMoneda } from '../../utils/numeros'
import { exportarVentasPosExcel } from '../../utils/exportarPos'
import {
  OPCIONES_MEDIO_PAGO_POS,
  formatearFechaPos,
  formatearKilosPos,
  formatearMontoPos,
  obtenerEstadoEnvasesPos,
  obtenerEstadoVentaPos,
  obtenerFechaActualPos,
  obtenerRangoFechaHoyPos,
  obtenerRangoFechaMesActualPos,
  obtenerRangoFechaSemanaActualPos,
  puedeEditarVentaPos,
} from '../../utils/pos'

function TarjetaResumen({ icono: Icono, etiqueta, valor }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <Icono className="text-emerald-600" />

      <p className="mt-5 text-sm text-slate-500">{etiqueta}</p>

      <p className="mt-1 text-2xl font-bold text-slate-900">
        {valor}
      </p>
    </div>
  )
}

function MiniDato({ etiqueta, valor, destacado = false }) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {etiqueta}
      </p>

      <p
        className={`mt-0.5 truncate ${
          destacado
            ? 'font-semibold text-slate-900'
            : 'text-slate-700'
        }`}
      >
        {valor}
      </p>
    </div>
  )
}

function VentasPos() {
  const { user } = useAuth()
  const { empresaActual } = useEmpresa()
  const location = useLocation()
  const navigate = useNavigate()
  const { modal, abrirModal } = useModalMensaje()

  const esConsulta = empresaActual?.rol === 'consulta'
  const esAdministrador = empresaActual?.rol === 'administrador'

  const [ventas, setVentas] = useState([])
  const [clientes, setClientes] = useState([])
  const [productos, setProductos] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [detallePorVenta, setDetallePorVenta] = useState(new Map())
  const [pagosPorVenta, setPagosPorVenta] = useState(new Map())
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [exportando, setExportando] = useState(false)

  // Filtros principales.
  const [busqueda, setBusqueda] = useState('')
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('')
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('')
  const [filtroEstadoPago, setFiltroEstadoPago] = useState('')
  const [filtroEstado, setFiltroEstado] = useState('registrado')

  // Filtros avanzados (colapsados en "Más filtros").
  const [mostrarMasFiltros, setMostrarMasFiltros] = useState(false)
  const [filtroClienteId, setFiltroClienteId] = useState('')
  const [filtroProductoId, setFiltroProductoId] = useState('')
  const [filtroMedioPago, setFiltroMedioPago] = useState('')
  const [filtroEstadoEnvases, setFiltroEstadoEnvases] = useState('')
  const [filtroUsuarioId, setFiltroUsuarioId] = useState('')
  const [filtroMontoDesde, setFiltroMontoDesde] = useState('')
  const [filtroMontoHasta, setFiltroMontoHasta] = useState('')
  const [filtroSaldo, setFiltroSaldo] = useState('')
  const [filtroDescuento, setFiltroDescuento] = useState('')

  const [ventaDetalle, setVentaDetalle] = useState(null)
  const [detalleAbierto, setDetalleAbierto] = useState(false)

  const [ventaEliminar, setVentaEliminar] = useState(null)
  const [eliminando, setEliminando] = useState(false)

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!empresaActual?.id) {
      setVentas([])
      setClientes([])
      setProductos([])
      setUsuarios([])
      setDetallePorVenta(new Map())
      setPagosPorVenta(new Map())
      setCargando(false)
      cerrarDetalle()
      return
    }

    cargarVentas()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActual?.id])

  useEffect(() => {
    if (!location.state) return

    if (location.state.recargar) {
      cargarVentas()
    }

    navigate('/pos/ventas', { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  const cargarVentas = async () => {
    if (!empresaActual?.id) return

    const empresaId = empresaActual.id

    try {
      setCargando(true)
      setError('')

      // Una sola consulta por tabla (nunca una por venta): la vista trae
      // el resumen completo y `ventas_pos_detalle` / `ventas_pos_pagos`
      // se cargan en bloque para poder filtrar y exportar por producto y
      // medio de pago sin N+1 (se cruzan en memoria contra `ventas`).
      const [
        respuestaVentas,
        respuestaClientes,
        respuestaProductos,
        respuestaDetalle,
        respuestaPagos,
      ] = await Promise.all([
        supabase
          .from('vw_ventas_pos_resumen')
          .select('*')
          .eq('empresa_id', empresaId)
          .order('fecha_venta', { ascending: false })
          .order('creado_en', { ascending: false }),
        supabase
          .from('clientes')
          .select('id, razon_social')
          .eq('empresa_id', empresaId)
          .eq('activo', true)
          .order('razon_social', { ascending: true }),
        supabase
          .from('productos')
          .select('id, codigo, nombre')
          .eq('empresa_id', empresaId)
          .eq('activo', true)
          .order('nombre', { ascending: true }),
        supabase
          .from('ventas_pos_detalle')
          .select('venta_id, producto_id, envase_nombre')
          .eq('empresa_id', empresaId),
        supabase
          .from('ventas_pos_pagos')
          .select('venta_id, medio_pago, monto')
          .eq('empresa_id', empresaId)
          .eq('estado', 'registrado'),
      ])

      if (respuestaVentas.error) throw respuestaVentas.error
      if (respuestaClientes.error) throw respuestaClientes.error
      if (respuestaProductos.error) throw respuestaProductos.error
      if (respuestaDetalle.error) throw respuestaDetalle.error
      if (respuestaPagos.error) throw respuestaPagos.error

      const ventasCargadas = respuestaVentas.data || []

      // Nombres de quien registró cada venta: una sola consulta a
      // `perfiles` con los ids ya presentes en `ventas` (no una por
      // usuario ni una por venta).
      const idsUsuarios = Array.from(
        new Set(
          ventasCargadas
            .map((venta) => venta.creado_por)
            .filter(Boolean)
        )
      )

      let usuariosCargados = []

      if (idsUsuarios.length > 0) {
        const respuestaUsuarios = await supabase
          .from('perfiles')
          .select('id, nombre_completo')
          .in('id', idsUsuarios)

        if (respuestaUsuarios.error) throw respuestaUsuarios.error

        usuariosCargados = respuestaUsuarios.data || []
      }

      if (!montadoRef.current) return

      const mapaDetalle = new Map()

      for (const fila of respuestaDetalle.data || []) {
        if (!mapaDetalle.has(fila.venta_id)) {
          mapaDetalle.set(fila.venta_id, [])
        }

        mapaDetalle.get(fila.venta_id).push({
          producto_id: fila.producto_id,
          envase_nombre: fila.envase_nombre,
        })
      }

      const mapaPagos = new Map()

      for (const fila of respuestaPagos.data || []) {
        if (!mapaPagos.has(fila.venta_id)) {
          mapaPagos.set(fila.venta_id, [])
        }

        mapaPagos.get(fila.venta_id).push({
          medio_pago: fila.medio_pago,
          monto: fila.monto,
        })
      }

      usuariosCargados.sort((a, b) =>
        (a.nombre_completo || '').localeCompare(
          b.nombre_completo || ''
        )
      )

      setVentas(ventasCargadas)
      setClientes(respuestaClientes.data || [])
      setProductos(respuestaProductos.data || [])
      setUsuarios(usuariosCargados)
      setDetallePorVenta(mapaDetalle)
      setPagosPorVenta(mapaPagos)
    } catch (errorCarga) {
      console.error('Error al cargar ventas POS:', errorCarga)

      if (!montadoRef.current) return

      setError(
        errorCarga.message || 'No fue posible cargar las ventas.'
      )
      setVentas([])
    } finally {
      if (montadoRef.current) {
        setCargando(false)
      }
    }
  }

  const verVenta = (venta) => {
    setVentaDetalle(venta)
    setDetalleAbierto(true)
  }

  const cerrarDetalle = () => {
    setDetalleAbierto(false)
    setVentaDetalle(null)
  }

  const solicitarEliminarVenta = (venta) => {
    setVentaEliminar(venta)
  }

  const confirmarEliminarVenta = async (motivo) => {
    if (!ventaEliminar) return

    try {
      setEliminando(true)

      const { error: errorRpc } = await supabase.rpc(
        'anular_venta_pos',
        {
          p_empresa_id: empresaActual.id,
          p_venta_id: ventaEliminar.id,
          p_motivo: motivo,
        }
      )

      if (errorRpc) throw errorRpc

      setVentaEliminar(null)

      await cargarVentas()

      abrirModal({
        tipo: 'success',
        titulo: 'Venta eliminada',
        mensaje: 'Venta eliminada correctamente.',
      })
    } catch (errorEliminar) {
      console.error('Error al eliminar venta POS:', errorEliminar)

      setVentaEliminar(null)

      abrirModal({
        tipo: 'error',
        titulo: 'No se pudo eliminar',
        mensaje:
          errorEliminar.message ||
          'No fue posible eliminar la venta.',
      })
    } finally {
      setEliminando(false)
    }
  }

  const productosPorId = useMemo(
    () => new Map(productos.map((producto) => [producto.id, producto])),
    [productos]
  )

  const usuariosPorId = useMemo(
    () =>
      new Map(
        usuarios.map((usuario) => [usuario.id, usuario.nombre_completo])
      ),
    [usuarios]
  )

  const terminoBusqueda = busqueda.trim().toLowerCase()

  // Productos cuyo nombre/código coincide con el término buscado: se usa
  // para poder buscar ventas por producto sin lanzar una consulta por
  // venta (se cruza contra `detallePorVenta`, ya cargado en bloque).
  const productosCoincidentes = useMemo(() => {
    if (!terminoBusqueda) return new Set()

    return new Set(
      productos
        .filter(
          (producto) =>
            (producto.nombre || '')
              .toLowerCase()
              .includes(terminoBusqueda) ||
            (producto.codigo || '')
              .toLowerCase()
              .includes(terminoBusqueda)
        )
        .map((producto) => producto.id)
    )
  }, [productos, terminoBusqueda])

  const montoDesdeNumero = filtroMontoDesde.trim()
    ? normalizarMoneda(filtroMontoDesde)
    : null
  const montoHastaNumero = filtroMontoHasta.trim()
    ? normalizarMoneda(filtroMontoHasta)
    : null

  const ventasFiltradas = useMemo(() => {
    return ventas.filter((venta) => {
      const detalles = detallePorVenta.get(venta.id)
      const pagos = pagosPorVenta.get(venta.id)

      const coincideBusqueda =
        !terminoBusqueda ||
        (venta.cliente_razon_social || '')
          .toLowerCase()
          .includes(terminoBusqueda) ||
        Boolean(
          detalles?.some((detalle) =>
            productosCoincidentes.has(detalle.producto_id)
          )
        )

      const coincideFechaDesde =
        !filtroFechaDesde || venta.fecha_venta >= filtroFechaDesde

      const coincideFechaHasta =
        !filtroFechaHasta || venta.fecha_venta <= filtroFechaHasta

      const coincideEstadoPago =
        !filtroEstadoPago || venta.estado_pago === filtroEstadoPago

      const coincideEstado =
        !filtroEstado || venta.estado === filtroEstado

      const coincideCliente =
        !filtroClienteId || venta.cliente_id === filtroClienteId

      const coincideProducto =
        !filtroProductoId ||
        Boolean(
          detalles?.some(
            (detalle) => detalle.producto_id === filtroProductoId
          )
        )

      const coincideMedioPago =
        !filtroMedioPago ||
        Boolean(
          pagos?.some((pago) => pago.medio_pago === filtroMedioPago)
        )

      const coincideEnvases =
        !filtroEstadoEnvases ||
        venta.estado_envases === filtroEstadoEnvases

      const coincideUsuario =
        !filtroUsuarioId || venta.creado_por === filtroUsuarioId

      const coincideMontoDesde =
        !Number.isFinite(montoDesdeNumero) ||
        (Number(venta.total_venta) || 0) >= montoDesdeNumero

      const coincideMontoHasta =
        !Number.isFinite(montoHastaNumero) ||
        (Number(venta.total_venta) || 0) <= montoHastaNumero

      const coincideSaldo =
        !filtroSaldo ||
        (filtroSaldo === 'con'
          ? (Number(venta.saldo_pendiente) || 0) > 0
          : (Number(venta.saldo_pendiente) || 0) <= 0)

      const coincideDescuento =
        !filtroDescuento ||
        (filtroDescuento === 'con'
          ? (Number(venta.monto_descuento) || 0) > 0
          : (Number(venta.monto_descuento) || 0) <= 0)

      return (
        coincideBusqueda &&
        coincideFechaDesde &&
        coincideFechaHasta &&
        coincideEstadoPago &&
        coincideEstado &&
        coincideCliente &&
        coincideProducto &&
        coincideMedioPago &&
        coincideEnvases &&
        coincideUsuario &&
        coincideMontoDesde &&
        coincideMontoHasta &&
        coincideSaldo &&
        coincideDescuento
      )
    })
  }, [
    ventas,
    terminoBusqueda,
    detallePorVenta,
    pagosPorVenta,
    productosCoincidentes,
    filtroFechaDesde,
    filtroFechaHasta,
    filtroEstadoPago,
    filtroEstado,
    filtroClienteId,
    filtroProductoId,
    filtroMedioPago,
    filtroEstadoEnvases,
    filtroUsuarioId,
    montoDesdeNumero,
    montoHastaNumero,
    filtroSaldo,
    filtroDescuento,
  ])

  // Orden principal fecha_venta DESC ya viene de la consulta; se agrega
  // creado_en DESC como criterio estable (evita saltos/duplicados entre
  // páginas si dos ventas comparten la misma fecha).
  const ventasOrdenadas = useMemo(() => {
    return [...ventasFiltradas].sort((a, b) => {
      if (a.fecha_venta !== b.fecha_venta) {
        return a.fecha_venta > b.fecha_venta ? -1 : 1
      }

      const creadoA = a.creado_en || ''
      const creadoB = b.creado_en || ''

      if (creadoA !== creadoB) {
        return creadoA > creadoB ? -1 : 1
      }

      return 0
    })
  }, [ventasFiltradas])

  const resumen = useMemo(() => {
    return ventasFiltradas.reduce(
      (acumulado, venta) => {
        acumulado.cantidad += 1
        acumulado.totalGeneral += Number(venta.total_venta) || 0
        acumulado.totalCobrado += Number(venta.monto_pagado) || 0
        acumulado.totalPendiente +=
          Number(venta.saldo_pendiente) || 0

        return acumulado
      },
      {
        cantidad: 0,
        totalGeneral: 0,
        totalCobrado: 0,
        totalPendiente: 0,
      }
    )
  }, [ventasFiltradas])

  const contadorFiltrosAvanzados = [
    filtroClienteId,
    filtroProductoId,
    filtroMedioPago,
    filtroEstadoEnvases,
    filtroUsuarioId,
    filtroMontoDesde.trim(),
    filtroMontoHasta.trim(),
    filtroSaldo,
    filtroDescuento,
  ].filter(Boolean).length

  const hayFiltrosPrincipalesActivos =
    Boolean(busqueda.trim()) ||
    Boolean(filtroFechaDesde) ||
    Boolean(filtroFechaHasta) ||
    Boolean(filtroEstadoPago) ||
    filtroEstado !== 'registrado'

  const hayFiltrosActivos =
    hayFiltrosPrincipalesActivos || contadorFiltrosAvanzados > 0

  // Cambia CUALQUIER filtro -> vuelve a página 1 (ver usePaginacion). El
  // cambio de página/tamaño de página nunca toca esta key ni los filtros.
  const filtrosKey = JSON.stringify([
    busqueda,
    filtroFechaDesde,
    filtroFechaHasta,
    filtroEstadoPago,
    filtroEstado,
    filtroClienteId,
    filtroProductoId,
    filtroMedioPago,
    filtroEstadoEnvases,
    filtroUsuarioId,
    filtroMontoDesde,
    filtroMontoHasta,
    filtroSaldo,
    filtroDescuento,
  ])

  const {
    pagina,
    setPagina,
    tamanioPagina,
    setTamanioPagina,
    totalPaginas,
    indiceInicio,
    indiceFin,
  } = usePaginacion({
    totalRegistros: ventasOrdenadas.length,
    filtrosKey,
  })

  const ventasPaginadas = useMemo(() => {
    const inicio = (pagina - 1) * tamanioPagina

    return ventasOrdenadas.slice(inicio, inicio + tamanioPagina)
  }, [ventasOrdenadas, pagina, tamanioPagina])

  const limpiarFiltros = () => {
    setBusqueda('')
    setFiltroFechaDesde('')
    setFiltroFechaHasta('')
    setFiltroEstadoPago('')
    setFiltroEstado('registrado')
    setFiltroClienteId('')
    setFiltroProductoId('')
    setFiltroMedioPago('')
    setFiltroEstadoEnvases('')
    setFiltroUsuarioId('')
    setFiltroMontoDesde('')
    setFiltroMontoHasta('')
    setFiltroSaldo('')
    setFiltroDescuento('')
  }

  const aplicarRangoFecha = (rango) => {
    if (
      filtroFechaDesde === rango.desde &&
      filtroFechaHasta === rango.hasta
    ) {
      setFiltroFechaDesde('')
      setFiltroFechaHasta('')
      return
    }

    setFiltroFechaDesde(rango.desde)
    setFiltroFechaHasta(rango.hasta)
  }

  const alternarEstadoPago = (valor) => {
    setFiltroEstadoPago((actual) => (actual === valor ? '' : valor))
  }

  const exportarExcel = async () => {
    if (exportando || ventasOrdenadas.length === 0) return

    try {
      setExportando(true)

      await exportarVentasPosExcel(
        ventasOrdenadas,
        { detallePorVenta, pagosPorVenta, productosPorId, usuariosPorId },
        `ventas-pos-${obtenerFechaActualPos()}.xlsx`
      )
    } catch (errorExportar) {
      console.error('Error al exportar ventas POS:', errorExportar)

      abrirModal({
        tipo: 'error',
        titulo: 'No se pudo exportar',
        mensaje:
          errorExportar.message ||
          'No fue posible generar el archivo Excel.',
      })
    } finally {
      setExportando(false)
    }
  }

  const rangoHoy = obtenerRangoFechaHoyPos()
  const rangoSemana = obtenerRangoFechaSemanaActualPos()
  const rangoMes = obtenerRangoFechaMesActualPos()

  const chipHoyActivo =
    filtroFechaDesde === rangoHoy.desde &&
    filtroFechaHasta === rangoHoy.hasta
  const chipSemanaActivo =
    filtroFechaDesde === rangoSemana.desde &&
    filtroFechaHasta === rangoSemana.hasta
  const chipMesActivo =
    filtroFechaDesde === rangoMes.desde &&
    filtroFechaHasta === rangoMes.hasta

  const renderFiltros = () => (
    <div className="mb-6 space-y-3">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <CampoFiltro
            label="Buscar ventas"
            className="sm:col-span-2 lg:col-span-1"
          >
            <div className="relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="text"
                value={busqueda}
                onChange={(event) => setBusqueda(event.target.value)}
                placeholder="Buscar por cliente..."
                className={estiloInputBusqueda}
              />
            </div>
          </CampoFiltro>

          <CampoFiltro label="Fecha desde">
            <input
              type="date"
              value={filtroFechaDesde}
              onChange={(event) =>
                setFiltroFechaDesde(event.target.value)
              }
              className={estiloInputFiltro}
            />
          </CampoFiltro>

          <CampoFiltro label="Fecha hasta">
            <input
              type="date"
              value={filtroFechaHasta}
              onChange={(event) =>
                setFiltroFechaHasta(event.target.value)
              }
              className={estiloInputFiltro}
            />
          </CampoFiltro>

          <CampoFiltro label="Estado de pago">
            <select
              value={filtroEstadoPago}
              onChange={(event) =>
                setFiltroEstadoPago(event.target.value)
              }
              className={estiloInputFiltro}
            >
              <option value="">Todos</option>
              <option value="pendiente">Pendiente</option>
              <option value="parcial">Parcial</option>
              <option value="pagado">Pagado</option>
            </select>
          </CampoFiltro>

          <CampoFiltro label="Estado venta">
            <select
              value={filtroEstado}
              onChange={(event) =>
                setFiltroEstado(event.target.value)
              }
              className={estiloInputFiltro}
            >
              <option value="registrado">Activas</option>
              <option value="anulado">Anuladas</option>
              <option value="">Todas</option>
            </select>
          </CampoFiltro>
        </div>

        <div className="-mx-1 mt-4 flex gap-2 overflow-x-auto px-1 pb-1">
          <ChipFiltro
            activo={chipHoyActivo}
            onClick={() => aplicarRangoFecha(rangoHoy)}
          >
            Hoy
          </ChipFiltro>

          <ChipFiltro
            activo={chipSemanaActivo}
            onClick={() => aplicarRangoFecha(rangoSemana)}
          >
            Esta semana
          </ChipFiltro>

          <ChipFiltro
            activo={chipMesActivo}
            onClick={() => aplicarRangoFecha(rangoMes)}
          >
            Este mes
          </ChipFiltro>

          <span className="mx-1 my-auto hidden h-5 w-px shrink-0 bg-slate-200 sm:block" />

          <ChipFiltro
            activo={filtroEstadoPago === 'pendiente'}
            onClick={() => alternarEstadoPago('pendiente')}
          >
            Pendientes
          </ChipFiltro>

          <ChipFiltro
            activo={filtroEstadoPago === 'parcial'}
            onClick={() => alternarEstadoPago('parcial')}
          >
            Parciales
          </ChipFiltro>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <button
            type="button"
            onClick={() => setMostrarMasFiltros((actual) => !actual)}
            className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-emerald-700"
          >
            <SlidersHorizontal size={16} />
            Más filtros
            {contadorFiltrosAvanzados > 0 &&
              ` (${contadorFiltrosAvanzados})`}
            {mostrarMasFiltros ? (
              <ChevronUp size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
          </button>

          {hayFiltrosActivos && (
            <button
              type="button"
              onClick={limpiarFiltros}
              className="text-sm font-semibold text-slate-500 hover:text-red-600"
            >
              Limpiar filtros
            </button>
          )}
        </div>

        {mostrarMasFiltros && (
          <div className="mt-4 grid grid-cols-1 gap-3 border-t border-slate-100 pt-4 sm:grid-cols-2 lg:grid-cols-3">
            <CampoFiltro label="Cliente">
              <select
                value={filtroClienteId}
                onChange={(event) =>
                  setFiltroClienteId(event.target.value)
                }
                className={estiloInputFiltro}
              >
                <option value="">Todos los clientes</option>

                {clientes.map((cliente) => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.razon_social}
                  </option>
                ))}
              </select>
            </CampoFiltro>

            <CampoFiltro label="Producto">
              <select
                value={filtroProductoId}
                onChange={(event) =>
                  setFiltroProductoId(event.target.value)
                }
                className={estiloInputFiltro}
              >
                <option value="">Todos los productos</option>

                {productos.map((producto) => (
                  <option key={producto.id} value={producto.id}>
                    {producto.codigo} — {producto.nombre}
                  </option>
                ))}
              </select>
            </CampoFiltro>

            <CampoFiltro label="Medio de pago">
              <select
                value={filtroMedioPago}
                onChange={(event) =>
                  setFiltroMedioPago(event.target.value)
                }
                className={estiloInputFiltro}
              >
                <option value="">Todos</option>

                {OPCIONES_MEDIO_PAGO_POS.map((opcion) => (
                  <option key={opcion.valor} value={opcion.valor}>
                    {opcion.etiqueta}
                  </option>
                ))}
              </select>
            </CampoFiltro>

            <CampoFiltro label="Cajas/Rejillas">
              <select
                value={filtroEstadoEnvases}
                onChange={(event) =>
                  setFiltroEstadoEnvases(event.target.value)
                }
                className={estiloInputFiltro}
              >
                <option value="">Todos</option>
                <option value="pendiente">Pendiente</option>
                <option value="pagado">Pagado</option>
              </select>
            </CampoFiltro>

            <CampoFiltro label="Registrada por">
              <select
                value={filtroUsuarioId}
                onChange={(event) =>
                  setFiltroUsuarioId(event.target.value)
                }
                className={estiloInputFiltro}
              >
                <option value="">Todos los usuarios</option>

                {usuarios.map((usuario) => (
                  <option key={usuario.id} value={usuario.id}>
                    {usuario.nombre_completo}
                  </option>
                ))}
              </select>
            </CampoFiltro>

            <div className="grid grid-cols-2 gap-3">
              <CampoFiltro label="Monto desde">
                <input
                  type="text"
                  inputMode="decimal"
                  value={filtroMontoDesde}
                  onChange={(event) =>
                    setFiltroMontoDesde(event.target.value)
                  }
                  placeholder="0"
                  className={estiloInputFiltro}
                />
              </CampoFiltro>

              <CampoFiltro label="Monto hasta">
                <input
                  type="text"
                  inputMode="decimal"
                  value={filtroMontoHasta}
                  onChange={(event) =>
                    setFiltroMontoHasta(event.target.value)
                  }
                  placeholder="0"
                  className={estiloInputFiltro}
                />
              </CampoFiltro>
            </div>

            <CampoFiltro label="Saldo pendiente">
              <select
                value={filtroSaldo}
                onChange={(event) =>
                  setFiltroSaldo(event.target.value)
                }
                className={estiloInputFiltro}
              >
                <option value="">Todos</option>
                <option value="con">Con saldo pendiente</option>
                <option value="sin">Sin saldo pendiente</option>
              </select>
            </CampoFiltro>

            <CampoFiltro label="Descuento">
              <select
                value={filtroDescuento}
                onChange={(event) =>
                  setFiltroDescuento(event.target.value)
                }
                className={estiloInputFiltro}
              >
                <option value="">Todos</option>
                <option value="con">Con descuento</option>
                <option value="sin">Sin descuento</option>
              </select>
            </CampoFiltro>
          </div>
        )}
      </div>

      <p className="text-sm text-slate-500">
        <span className="font-semibold text-slate-700">
          {ventasFiltradas.length}
        </span>{' '}
        {ventasFiltradas.length === 1
          ? 'venta encontrada'
          : 'ventas encontradas'}
      </p>
    </div>
  )

  const renderContenido = () => {
    if (cargando) {
      return (
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <RefreshCw
              size={38}
              className="mx-auto animate-spin text-emerald-600"
            />

            <p className="mt-4 font-medium text-slate-500">
              Cargando ventas...
            </p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
          <p className="font-semibold text-red-700">{error}</p>

          <button
            type="button"
            onClick={cargarVentas}
            className="mt-4 rounded-lg bg-slate-800 px-5 py-2 font-semibold text-white transition hover:bg-slate-900"
          >
            Reintentar
          </button>
        </div>
      )
    }

    if (ventas.length === 0) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
            <ShoppingCart
              size={28}
              className="text-emerald-600"
            />
          </div>

          <h3 className="mt-5 text-lg font-bold text-slate-900">
            No hay ventas registradas
          </h3>

          <p className="mt-2 text-slate-500">
            {esConsulta
              ? 'Aún no se han registrado ventas.'
              : 'Presiona "Nueva venta" para registrar la primera venta.'}
          </p>

          {!esConsulta && (
            <Link
              to="/pos/nueva-venta"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-600 px-5 py-3 font-semibold text-emerald-700 transition hover:bg-emerald-50"
            >
              <Plus size={19} />
              Registrar primera venta
            </Link>
          )}
        </div>
      )
    }

    return (
      <>
        {renderFiltros()}

        {ventasOrdenadas.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <p className="text-slate-500">
              No se encontraron ventas para los filtros aplicados.
            </p>

            {hayFiltrosActivos && (
              <button
                type="button"
                onClick={limpiarFiltros}
                className="mt-4 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Mobile: tarjetas apiladas en vez de tabla ancha. */}
            <div className="space-y-3 md:hidden">
              {ventasPaginadas.map((venta) => {
                const estadoPago = obtenerEstadoPago(
                  venta.estado_pago
                )
                const estadoVenta = obtenerEstadoVentaPos(
                  venta.estado
                )
                const estadoEnvases = obtenerEstadoEnvasesPos(
                  venta.estado_envases
                )

                return (
                  <div
                    key={venta.id}
                    className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        {venta.cliente_razon_social ? (
                          <p className="truncate font-semibold text-slate-900">
                            {venta.cliente_razon_social}
                          </p>
                        ) : (
                          <p className="text-slate-400">
                            Sin cliente
                          </p>
                        )}

                        <p className="mt-0.5 text-xs text-slate-500">
                          {formatearFechaPos(venta.fecha_venta)}
                        </p>
                      </div>

                      <span
                        className={`shrink-0 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${estadoPago.clase}`}
                      >
                        {estadoPago.etiqueta}
                      </span>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3 text-sm">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                          Total
                        </p>
                        <p className="mt-0.5 truncate font-semibold text-slate-900">
                          {formatearMontoPos(venta.total_venta)}
                        </p>
                        {venta.tipo_descuento &&
                          venta.tipo_descuento !== 'ninguno' && (
                            <p className="mt-0.5 truncate text-xs text-emerald-600">
                              Desc.{' '}
                              {formatearMontoPos(
                                venta.monto_descuento
                              )}
                            </p>
                          )}
                      </div>

                      <MiniDato
                        etiqueta="Saldo"
                        valor={formatearMontoPos(
                          venta.saldo_pendiente
                        )}
                      />

                      <MiniDato
                        etiqueta="Kilos"
                        valor={formatearKilosPos(
                          venta.total_kilos_netos
                        )}
                      />

                      <MiniDato
                        etiqueta="Estado"
                        valor={estadoVenta.etiqueta}
                      />

                      <MiniDato
                        etiqueta="Cajas/Rejillas"
                        valor={estadoEnvases.etiqueta}
                      />
                    </div>

                    <div className="mt-4 flex flex-col gap-2">
                      <button
                        type="button"
                        onClick={() => verVenta(venta)}
                        className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
                      >
                        <Eye size={16} />
                        Ver detalle
                      </button>

                      {!esConsulta &&
                        puedeEditarVentaPos(venta, {
                          esAdministrador,
                          usuarioId: user?.id,
                        }) && (
                          <Link
                            to={`/pos/ventas/${venta.id}/editar`}
                            className="flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-600 px-3 py-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50"
                          >
                            <Pencil size={16} />
                            Editar
                          </Link>
                        )}

                      {!esConsulta &&
                        venta.estado === 'registrado' && (
                          <button
                            type="button"
                            onClick={() =>
                              solicitarEliminarVenta(venta)
                            }
                            className="flex w-full items-center justify-center gap-2 rounded-lg border border-red-200 px-3 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                            Eliminar venta
                          </button>
                        )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Tablet / desktop: tabla completa. */}
            <div className="hidden w-full overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm md:block">
            <table className="w-full text-left text-sm lg:min-w-[1120px]">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3">Fecha</th>
                  <th className="px-6 py-3">Cliente</th>
                  <th className="hidden px-6 py-3 lg:table-cell">
                    Productos
                  </th>
                  <th className="hidden px-6 py-3 lg:table-cell">
                    Kilos
                  </th>
                  <th className="px-6 py-3 text-right">Total</th>
                  <th className="hidden px-6 py-3 text-right lg:table-cell">
                    Pagado
                  </th>
                  <th className="hidden px-6 py-3 text-right lg:table-cell">
                    Saldo
                  </th>
                  <th className="px-6 py-3">Estado pago</th>
                  <th className="hidden px-6 py-3 xl:table-cell">
                    Cajas/Rejillas
                  </th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-right">
                    Acciones
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {ventasPaginadas.map((venta) => {
                  const estadoPago = obtenerEstadoPago(
                    venta.estado_pago
                  )
                  const estadoVenta = obtenerEstadoVentaPos(
                    venta.estado
                  )
                  const estadoEnvases = obtenerEstadoEnvasesPos(
                    venta.estado_envases
                  )

                  return (
                    <tr key={venta.id}>
                      <td className="whitespace-nowrap px-6 py-4 text-slate-600">
                        {formatearFechaPos(venta.fecha_venta)}
                      </td>

                      <td className="px-6 py-4">
                        {venta.cliente_razon_social ? (
                          <p className="font-semibold text-slate-900">
                            {venta.cliente_razon_social}
                          </p>
                        ) : (
                          <span className="text-slate-400">
                            Sin cliente
                          </span>
                        )}
                      </td>

                      <td className="hidden whitespace-nowrap px-6 py-4 text-slate-600 lg:table-cell">
                        {venta.cantidad_items}
                      </td>

                      <td className="hidden whitespace-nowrap px-6 py-4 text-slate-600 lg:table-cell">
                        {formatearKilosPos(venta.total_kilos_netos)}
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-right font-semibold text-slate-900">
                        {formatearMontoPos(venta.total_venta)}
                        {venta.tipo_descuento &&
                          venta.tipo_descuento !== 'ninguno' && (
                            <p className="mt-0.5 text-xs font-normal text-emerald-600">
                              Desc.{' '}
                              {formatearMontoPos(
                                venta.monto_descuento
                              )}
                            </p>
                          )}
                      </td>

                      <td className="hidden whitespace-nowrap px-6 py-4 text-right text-slate-600 lg:table-cell">
                        {formatearMontoPos(venta.monto_pagado)}
                      </td>

                      <td className="hidden whitespace-nowrap px-6 py-4 text-right text-slate-600 lg:table-cell">
                        {formatearMontoPos(venta.saldo_pendiente)}
                      </td>

                      <td className="whitespace-nowrap px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${estadoPago.clase}`}
                        >
                          {estadoPago.etiqueta}
                        </span>
                      </td>

                      <td className="hidden whitespace-nowrap px-6 py-4 xl:table-cell">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${estadoEnvases.clase}`}
                        >
                          {estadoEnvases.etiqueta}
                        </span>
                      </td>

                      <td className="hidden whitespace-nowrap px-6 py-4 md:table-cell">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${estadoVenta.clase}`}
                        >
                          {estadoVenta.etiqueta}
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => verVenta(venta)}
                            title="Ver detalle"
                            aria-label="Ver detalle de la venta"
                            className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
                          >
                            <Eye size={16} />
                          </button>

                          {!esConsulta &&
                            puedeEditarVentaPos(venta, {
                              esAdministrador,
                              usuarioId: user?.id,
                            }) && (
                              <Link
                                to={`/pos/ventas/${venta.id}/editar`}
                                title="Editar venta"
                                aria-label="Editar venta"
                                className="flex items-center gap-2 rounded-lg border border-emerald-600 px-3 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50"
                              >
                                <Pencil size={16} />
                              </Link>
                            )}

                          {!esConsulta &&
                            venta.estado === 'registrado' && (
                              <button
                                type="button"
                                onClick={() =>
                                  solicitarEliminarVenta(venta)
                                }
                                title="Eliminar venta"
                                aria-label="Eliminar venta"
                                className="flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            </div>

            <Paginacion
              pagina={pagina}
              totalPaginas={totalPaginas}
              tamanioPagina={tamanioPagina}
              totalRegistros={ventasOrdenadas.length}
              indiceInicio={indiceInicio}
              indiceFin={indiceFin}
              etiquetaRegistros="ventas"
              onCambiarPagina={setPagina}
              onCambiarTamanio={setTamanioPagina}
            />
          </>
        )}
      </>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            POS / Ventas
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Ventas
          </h2>

          <p className="mt-2 text-slate-500">
            Ventas registradas para{' '}
            {empresaActual?.nombre_fantasia}.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={cargarVentas}
            disabled={cargando}
            className="flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw
              size={18}
              className={cargando ? 'animate-spin' : ''}
            />
            Actualizar
          </button>

          <button
            type="button"
            onClick={exportarExcel}
            disabled={exportando || ventasOrdenadas.length === 0}
            title={
              ventasOrdenadas.length === 0
                ? 'No hay registros para exportar.'
                : undefined
            }
            className="flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FileSpreadsheet size={18} />
            {exportando ? (
              'Exportando...'
            ) : (
              <>
                Exportar<span className="hidden sm:inline"> Excel</span>
              </>
            )}
          </button>

          {!esConsulta && (
            <Link
              to="/pos/nueva-venta"
              className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700"
            >
              <Plus size={20} />
              Nueva venta
            </Link>
          )}
        </div>
      </div>

      {!cargando && !error && ventas.length > 0 && (
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <TarjetaResumen
            icono={ShoppingCart}
            etiqueta="Cantidad de ventas"
            valor={resumen.cantidad}
          />

          <TarjetaResumen
            icono={BadgeCheck}
            etiqueta="Total ventas"
            valor={formatearMontoPos(resumen.totalGeneral)}
          />

          <TarjetaResumen
            icono={BadgeCheck}
            etiqueta="Total pagado"
            valor={formatearMontoPos(resumen.totalCobrado)}
          />

          <TarjetaResumen
            icono={Clock}
            etiqueta="Total pendiente"
            valor={formatearMontoPos(resumen.totalPendiente)}
          />
        </div>
      )}

      {renderContenido()}

      <DetalleVentaPosModal
        abierto={detalleAbierto}
        venta={ventaDetalle}
        puedeAnular={!esConsulta}
        onCerrar={cerrarDetalle}
        onCambio={cargarVentas}
      />

      <AnularVentaPosModal
        abierto={Boolean(ventaEliminar)}
        venta={ventaEliminar}
        procesando={eliminando}
        onCancelar={() => setVentaEliminar(null)}
        onConfirmar={confirmarEliminarVenta}
      />

      <ModalMensaje {...modal} />
    </div>
  )
}

export default VentasPos
